import { useRef } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";

interface ImageUploaderProps {
  src?: string | null;
  onFileSelect: (file: File) => void;
  isLoading?: boolean;
  size?: number;
  placeholder?: string;
  accept?: string;
  maxSizeMB?: number;
  onMaxSizeExceeded?: () => void;
}

const ImageUploader = ({
  src,
  onFileSelect,
  isLoading = false,
  size = 140,
  placeholder,
  accept = "image/jpeg,image/jpg,image/png,image/svg+xml,image/webp",
  maxSizeMB = 2,
  onMaxSizeExceeded,
}: ImageUploaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
      onMaxSizeExceeded?.();
      e.target.value = "";
      return;
    }

    onFileSelect(file);
    e.target.value = "";
  };

  return (
    <Box position="relative" width={size} height={size}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        onChange={handleChange}
      />

      <Avatar
        src={src ?? undefined}
        sx={{
          borderRadius: 1,
          width: size,
          height: size,
          cursor: "pointer",
          "&:hover": { opacity: 0.85 },
        }}
        variant="square"
        onClick={handleClick}
      >
        {!src && (
          <Stack alignItems="center" spacing={0.5}>
            <CameraAltOutlinedIcon sx={{ fontSize: 32, color: "#98A2B3" }} />
            {placeholder && (
              <Typography fontSize="11px" color="#98A2B3">
                {placeholder}
              </Typography>
            )}
          </Stack>
        )}
      </Avatar>

      {isLoading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(255,255,255,0.7)"
          borderRadius={1}
        >
          <CircularProgress size={28} />
        </Box>
      )}
    </Box>
  );
};

export default ImageUploader;
